import type { ReactNode } from "react";
import { classes } from "../../nucleo/classes";

export interface ItemDeAba<C extends string = string> {
  chave: C;
  rotulo: ReactNode;
  contagem?: number;
}

export interface PropsDasAbas<C extends string = string> {
  /** Do que este conjunto de abas trata — vai para o `aria-label` da lista. */
  rotulo: string;
  itens: ReadonlyArray<ItemDeAba<C>>;
  ativa: C;
  aoSelecionar: (chave: C) => void;
  /** Prefixo dos ids que ligam aba e painel. */
  id: string;
  className?: string;
}

export function Abas<C extends string = string>({
  rotulo,
  itens,
  ativa,
  aoSelecionar,
  id,
  className,
}: PropsDasAbas<C>) {
  return (
    <div className={classes("abas", className)} role="tablist" aria-label={rotulo}>
      {itens.map((item) => {
        const selecionada = item.chave === ativa;
        return (
          <button
            key={item.chave}
            type="button"
            role="tab"
            id={`${id}-aba-${item.chave}`}
            aria-selected={selecionada}
            aria-controls={`${id}-painel-${item.chave}`}
            // Só a aba ativa recebe foco pelo Tab; entre abas navega-se com as setas,
            // que é o que o padrão de tablist manda.
            tabIndex={selecionada ? 0 : -1}
            className={classes("abas__item", selecionada && "abas__item--ativa")}
            onClick={() => aoSelecionar(item.chave)}
            onKeyDown={(evento) => {
              /* A partir da aba que recebeu a tecla, e não da `ativa`: com duas teclas seguidas
                 a seleção pode ainda não ter voltado como prop, mas o foco já andou. */
              const atual = itens.findIndex((i) => i.chave === item.chave);
              const destino =
                evento.key === "ArrowRight"
                  ? (atual + 1) % itens.length
                  : evento.key === "ArrowLeft"
                    ? (atual - 1 + itens.length) % itens.length
                    : evento.key === "Home"
                      ? 0
                      : evento.key === "End"
                        ? itens.length - 1
                        : null;
              if (destino === null) return;
              evento.preventDefault();
              const proximo = itens[destino];
              if (!proximo) return;
              aoSelecionar(proximo.chave);
              /* O foco acompanha a seleção. Sem isso ele ficava na aba antiga — agora com
                 `tabIndex=-1` —, e a seta seguinte partia de um lugar que não é mais o ativo. */
              document.getElementById(`${id}-aba-${proximo.chave}`)?.focus();
            }}
          >
            {item.rotulo}
            {typeof item.contagem === "number" ? (
              <span className="abas__contagem">{item.contagem}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function PainelDaAba({
  id,
  chave,
  ativa,
  children,
  className,
  "data-testid": testid,
}: {
  id: string;
  chave: string;
  ativa: string;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  if (chave !== ativa) return null;
  return (
    <div
      data-testid={testid}
      role="tabpanel"
      id={`${id}-painel-${chave}`}
      aria-labelledby={`${id}-aba-${chave}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}
